// src/utils/viewAs.js
//
// "View as": an Admin picks a person (say Ryan) and Home shows exactly what
// Ryan sees (his role, his jobs, his clock and check-ins, his language and
// pay panel) without his password. It is READ-ONLY: every save path in the
// app calls blockIfViewingAs() first and stops.
//
// Stored per browser tab (sessionStorage), so closing the tab ends it.
// App.js clears it for anyone who isn't an Admin, so it can't be abused.
//
//   startViewAs(person)   person = { uid, docId, name, role, language }
//   stopViewAs()
//   getViewAs()           -> person | null
//   isViewingAs()         -> bool
//   blockIfViewingAs()    -> true (and tells the person) when a save must stop

import { useEffect, useState } from "react";
import { tNow } from "../i18n/LanguageContext";

const KEY = "servicing_view_as";

const read = () => {
  try {
    const raw = sessionStorage.getItem(KEY);
    if (!raw) return null;
    const v = JSON.parse(raw);
    return v && v.uid ? v : null;
  } catch (e) {
    return null;
  }
};

let current = read();
const listeners = new Set();
const emit = () => listeners.forEach((fn) => fn(current));

export const getViewAs = () => current;
export const isViewingAs = () => Boolean(current);

/** Start viewing as someone, then reload so every screen loads as them. */
export const startViewAs = (person, { reload = true } = {}) => {
  current = {
    uid: person.uid,
    docId: person.docId || person.uid,
    name: person.name || "",
    role: person.role || "",
    language: person.language === "es" ? "es" : "en",
  };
  try {
    sessionStorage.setItem(KEY, JSON.stringify(current));
  } catch (e) {
    /* private mode: works until reload */
  }
  emit();
  if (reload) window.location.assign("/");
};

export const stopViewAs = ({ reload = true } = {}) => {
  const was = current;
  current = null;
  try {
    sessionStorage.removeItem(KEY);
  } catch (e) {
    /* ignore */
  }
  emit();
  if (reload && was) window.location.assign("/");
};

/** For save handlers: `if (blockIfViewingAs()) return;` */
export const blockIfViewingAs = () => {
  if (!current) return false;
  try {
    window.alert(
      tNow("View as is read-only. Nothing was saved. (You're viewing as {name}.)", { name: current.name })
    );
  } catch (e) {
    /* ignore */
  }
  return true;
};

/** Same check without the pop-up, for background work (GPS, auto check-in). */
export const silentlyBlockedByViewAs = () => Boolean(current);

export const useViewAs = () => {
  const [v, setV] = useState(current);
  useEffect(() => {
    listeners.add(setV);
    return () => listeners.delete(setV);
  }, []);
  return v;
};
